import type { ElementType } from "react";
import type { Theme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import { Box, Card, Stack, Typography, alpha, styled } from "@mui/material";
import Link from "next/link";

import SafeImage from "@/components/common/SafeImage";

import {
  CARD_BORDER_RADIUS,
  MOBILE_BREAKPOINT,
  PAGE_PADDING,
  SMALL_PHONE_WIDTH,
} from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";

/* -------------------------------- keyframes ------------------------------- */

export const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const pulseRing = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.04);
    opacity: 0.9;
  }
`;

/* ------------------------------ page background ---------------------------- */

export const CoverBackground = styled(Stack)<{ component?: ElementType }>(
  ({ theme }) => ({
    position: "relative",
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",
    touchAction: "pan-y",
    height: "100%",
    minHeight: "100dvh",
    width: "100%",
    flex: 1,
    padding: PAGE_PADDING,
    backgroundColor: theme.palette.background.default,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // CSS cannot interpolate background-image URLs. Declaring a transition
    // here caused some WebViews to briefly repaint the fallback between URLs.
    transition: "none",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      // Verbatim from the original's own landing chunk
      // (`_next/static/chunks/pages/index-*.js`, mined into
      // `recovery-source/index-formatted.js`) — a diagonal scrim whose strength
      // depends on the palette mode. An earlier pass measured a vertical
      // `to top` scrim on a *different tenant's* older deployment and wrote
      // that in instead; both the captured dist and the current live build of
      // this codebase carry the 135deg pair below.
      background:
        theme.palette.mode === "dark"
          ? "linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)"
          : "linear-gradient(135deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 100%)",
      zIndex: Z_INDEX.NONE,
      pointerEvents: "none",
      opacity: 0,
      transition: "opacity 0.2s ease-out",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background:
        theme.palette.mode === "dark"
          ? `radial-gradient(circle at 18% 12%, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 46%)`
          : `radial-gradient(circle at 18% 12%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 48%)`,
      zIndex: Z_INDEX.NONE,
      pointerEvents: "none",
      opacity: 0.65,
      transition: "opacity 0.2s ease-out",
    },
    "&.loaded::before": { opacity: 1 },
    "&.loaded::after": { opacity: 1 },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      justifyContent: "flex-end",
      padding: 0,
      // Under `md` the original swaps the diagonal scrim for a vertical one
      // that darkens towards the bottom, where the card meets the photo.
      "&::before": {
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)"
            : "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)",
      },
    },
  }),
);

/* --------------------------------- the card -------------------------------- */

export const LandingCard = styled(Stack)(({ theme }) => ({
  position: "relative",
  zIndex: Z_INDEX.LOW,
  width: "100%",
  maxWidth: 480,
  flexShrink: 0,
  minHeight: 500,
  overflowX: "hidden",
  overflowY: "visible",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorY: "contain",
  padding: PAGE_PADDING,
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderRadius: CARD_BORDER_RADIUS,
  boxShadow:
    theme.palette.mode === "dark"
      ? `0 20px 50px ${alpha(theme.palette.common.black, 0.4)}`
      : `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
  background: theme.palette.background.paper,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  outline: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.light, 0.6)})`,
    zIndex: 1,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: -80,
    right: -80,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: alpha(theme.palette.primary.main, 0.06),
    filter: "blur(50px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  opacity: 1,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    borderRadius: "30px 30px 0 0",
    width: "100vw",
    maxWidth: "100vw",
    minHeight: 500,
    WebkitOverflowScrolling: "touch",
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.75),
  },
  "@media (max-height: 760px)": {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.25),
  },
}));

/* ---------------------------------- header --------------------------------- */

export const HeaderSection = styled(Stack)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  width: "100%",
  alignItems: "center",
  gap: theme.spacing(0.85),
  paddingBottom: theme.spacing(1.5),
  opacity: 1,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    gap: theme.spacing(0.6),
    paddingBottom: theme.spacing(1),
  },
  "@media (max-height: 760px)": {
    gap: theme.spacing(0.45),
    paddingBottom: theme.spacing(0.5),
  },
}));

export const LogoWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: "relative",
  width: 88,
  height: 88,
  borderRadius: "50%",
  overflow: "visible",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.18)}, 0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  border: `3px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    border: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    opacity: 0.75,
    pointerEvents: "none",
  },
  opacity: 1,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    marginBottom: theme.spacing(1),
    width: 72,
    height: 72,
  },
  "@media (max-height: 760px)": {
    marginBottom: theme.spacing(0.75),
    width: 64,
    height: 64,
  },
}));

export const LogoImage = styled(SafeImage)({
  position: "relative",
  zIndex: 2,
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  objectFit: "cover",
});

export const LogoInitial = styled(Typography)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  color:
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.primary.dark,
  textTransform: "uppercase",
  textShadow:
    theme.palette.mode === "dark"
      ? `0 2px 12px ${alpha(theme.palette.common.black, 0.5)}`
      : "none",
}));

export const WelcomeText = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.7),
  textAlign: "center",
  letterSpacing: 1.2,
  fontSize: "0.85rem",
  fontWeight: 500,
  textTransform: "uppercase",
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { fontSize: "0.8rem" },
}));

export const BusinessName = styled(Typography)<{ component?: ElementType }>(
  ({ theme }) => ({
    color: theme.palette.text.primary,
    textAlign: "center",
    letterSpacing: -0.5,
    lineHeight: 1.2,
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      fontSize: "1.95rem",
      lineHeight: 1.15,
    },
    [theme.breakpoints.down(SMALL_PHONE_WIDTH)]: { fontSize: "1.75rem" },
  }),
);

export const Tagline = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: "center",
  maxWidth: 360,
  marginTop: theme.spacing(0.75),
  lineHeight: 1.45,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    maxWidth: 300,
    fontSize: "0.92rem",
    lineHeight: 1.35,
    marginTop: theme.spacing(0.5),
  },
  [theme.breakpoints.down(SMALL_PHONE_WIDTH)]: { fontSize: "0.86rem" },
}));

export const ChipRow = styled(Stack)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(1.5),
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  rowGap: theme.spacing(0.75),
  columnGap: theme.spacing(0.75),
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    marginTop: theme.spacing(0.75),
    rowGap: theme.spacing(0.5),
    columnGap: theme.spacing(0.5),
  },
}));

const chipBase = (theme: Theme) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  borderRadius: 999,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  background: alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.black,
    0.05,
  ),
  color: theme.palette.text.secondary,
  padding: `${theme.spacing(0.4)} ${theme.spacing(1)}`,
  fontSize: "0.75rem",
  whiteSpace: "nowrap",
});

export const RatingChip = styled(Box)(({ theme }) => chipBase(theme));

export const StatusChip = styled("button")(({ theme }) => ({
  ...chipBase(theme),
  font: "inherit",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(
      theme.palette.mode === "dark"
        ? theme.palette.common.white
        : theme.palette.common.black,
      0.1,
    ),
  },
  "&:active": { transform: "scale(0.97)" },
  "&:focus-visible": {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.55)}`,
    outlineOffset: 2,
  },
}));

export const StatusDot = styled("span")({
  width: 8,
  height: 8,
  borderRadius: "50%",
  display: "inline-block",
});

/* ------------------------------ order type list ---------------------------- */

export const OrderSection = styled(Stack)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  width: "100%",
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1.5),
  opacity: 1,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    gap: theme.spacing(1.25),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1),
  },
}));

export const SectionDivider = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(0.5),
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.3)}, transparent)`,
  },
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { display: "none" },
}));

export const SectionLabel = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.7),
  textAlign: "center",
  fontSize: "0.7rem",
  letterSpacing: 1.6,
  textTransform: "uppercase",
  flexShrink: 0,
  whiteSpace: "nowrap",
}));

export const OrderTypeList = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { gap: theme.spacing(1.5) },
  "& a:focus-visible .order-type-card": {
    borderColor: "currentColor",
    outline: "2px solid currentColor",
    outlineOffset: 2,
  },
  "& > a": { opacity: 1 },
}));

export const OrderTypeCard = styled(Card)(({ theme }) => ({
  position: "relative",
  width: "100%",
  cursor: "pointer",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  borderRadius: 14,
  overflow: "hidden",
  textDecoration: "none",
  background: alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.04 : 0.02,
  ),
  outline: "none",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 12,
    bottom: 12,
    left: 0,
    width: 4,
    borderRadius: "0 4px 4px 0",
    background: theme.palette.primary.main,
    transition: "all 0.3s ease",
    opacity: 0.7,
  },
  "&.takeaway-card::before": { background: theme.palette.info.main },
  "&.delivery-card::before": { background: theme.palette.primary.main },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? `0 14px 28px ${alpha(theme.palette.common.black, 0.5)}`
        : `0 12px 24px ${alpha(theme.palette.common.black, 0.18)}`,
    borderColor: alpha(theme.palette.primary.main, 0.4),
    "&::before": { opacity: 1, top: 8, bottom: 8, width: 5 },
    "& .arrow-circle": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    "& .icon-circle": {
      transform: "scale(1.06)",
      boxShadow: `0 12px 22px ${alpha(theme.palette.primary.main, 0.28)}`,
    },
    "& .card-content": { transform: "translateY(-1px)" },
  },
  "&:active": { transform: "translateY(-2px)" },
  "&.reservation-card": {
    border: `1.5px solid ${alpha(theme.palette.primary.main, 0.42)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 68%)`,
    boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.14)}`,
    "&::before": { display: "none" },
    "&::after": {
      content: '""',
      position: "absolute",
      top: -42,
      right: -32,
      width: 120,
      height: 120,
      borderRadius: "50%",
      background: alpha(theme.palette.primary.main, 0.16),
      transition: "transform 0.3s ease",
      pointerEvents: "none",
      [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        top: -28,
        right: -28,
        width: 80,
        height: 80,
      },
    },
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: alpha(theme.palette.primary.main, 0.65),
      boxShadow: `0 16px 28px ${alpha(theme.palette.primary.main, 0.24)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.24)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 70%)`,
      "&::after": { transform: "scale(1.1)" },
      "& .arrow-circle": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
      "& .icon-circle": {
        transform: "scale(1.06)",
        boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
      },
      "& .card-content": { transform: "translateY(-1px)" },
    },
    "&:active": { transform: "translateY(-1px)" },
  },
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { borderRadius: 12 },
}));

export const CardContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5),
  position: "relative",
  transition: "transform 0.25s ease",
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    padding: theme.spacing(1.5),
    gap: theme.spacing(1.25),
  },
  "@media (max-height: 760px)": {
    padding: theme.spacing(1.25),
    gap: theme.spacing(1),
  },
}));

export const IconCircle = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
  transition: "all 0.25s ease",
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    width: 44,
    height: 44,
    "& svg": { fontSize: "24px !important" },
  },
}));

export const CardText = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

export const CardBadge = styled(Typography)(({ theme }) => ({
  alignSelf: "flex-start",
  textTransform: "uppercase",
  letterSpacing: 1,
  fontWeight: 700,
  fontSize: "0.65rem",
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.12),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
  borderRadius: 999,
  padding: "2px 8px",
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    fontSize: "0.6rem",
    letterSpacing: 0.6,
    padding: "1px 6px",
  },
}));

export const CardTitle = styled(Typography)<{ component?: ElementType }>(
  ({ theme }) => ({
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { fontSize: "1.15rem" },
    [theme.breakpoints.down(SMALL_PHONE_WIDTH)]: { fontSize: "1.05rem" },
  }),
);

export const CardSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: { display: "none" },
}));

export const CardEta = styled(Typography)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.4),
  fontSize: "0.75rem",
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginTop: theme.spacing(0.25),
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    fontSize: "0.78rem",
    marginTop: 0,
  },
}));

export const ArrowCircle = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  transition: "all 0.3s ease",
  "& svg": { fontSize: "20px !important" },
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    width: 34,
    height: 34,
    "& svg": { fontSize: "17px !important" },
  },
}));

/* ---------------------------------- footer --------------------------------- */

export const FooterSection = styled(Stack)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  width: "100%",
  alignItems: "center",
  paddingTop: theme.spacing(1.5),
  marginTop: "auto",
  opacity: 1,
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    paddingTop: theme.spacing(0.75),
  },
  "@media (max-height: 760px)": { paddingTop: theme.spacing(0.5) },
}));

export const FooterText = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.9),
  textAlign: "center",
  [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
    fontSize: "0.8rem",
    lineHeight: 1.3,
  },
}));

export const StoreBadgeLink = styled(Link)({
  display: "inline-flex",
  borderRadius: 8,
  transition: "opacity 0.2s ease, transform 0.2s ease",
  "&:hover": { opacity: 0.92, transform: "translateY(-1px)" },
  "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: 2 },
});
